import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AssessmentSetup } from '../../interfaces/career-setup/assessment-setup.interface';
import { ApiResponse } from '../../interfaces/ApiResponse';
import { PageResponse } from '../../interfaces/page-response.interface';
import { environment } from '../../../../environments/environment.dev';
import { convertToHttpParams, CommonSearchParam } from '../../interfaces/common-search-param.interface';
import { AssessmentSetupService } from './assessment-setup.service';

@Injectable({
  providedIn: 'root'
})
export class AssessmentSetupApiService extends AssessmentSetupService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}v1/assessment-setups`;

  constructor() {
    super();
  }

  override findAll(params: CommonSearchParam): Observable<PageResponse<AssessmentSetup>> {
    const httpParams = convertToHttpParams(params);
    return this.wrapRequest(
      this.http.get<ApiResponse<PageResponse<AssessmentSetup>>>(this.baseUrl, {
        headers: this.headers,
        params: httpParams
      }).pipe(
        map(res => res?.response || { content: [], page: { number: 0, size: 0, totalElements: 0, totalPages: 0 } })
      ),
      false,
      false
    );
  }

  override find(id: string): Observable<AssessmentSetup> {
    return this.wrapRequest(
      this.http.get<ApiResponse<AssessmentSetup>>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override create(data: Partial<AssessmentSetup>): Observable<AssessmentSetup> {
    return this.wrapRequest(
      this.http.post<ApiResponse<AssessmentSetup>>(this.baseUrl, data, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override update(id: string, data: Partial<AssessmentSetup>): Observable<AssessmentSetup> {
    return this.wrapRequest(
      this.http.put<ApiResponse<AssessmentSetup>>(`${this.baseUrl}/${id}`, data, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override updateStatus(id: string, isActive: boolean): Observable<AssessmentSetup> {
    return this.wrapRequest(
      this.http.patch<ApiResponse<AssessmentSetup>>(`${this.baseUrl}/${id}/status`, null, {
        headers: this.headers,
        params: { isActive: isActive.toString() }
      }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override delete(id: string): Observable<void> {
    return this.wrapRequest(
      this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
        map(() => undefined)
      ),
      false,
      false
    );
  }
}
